<script setup lang="ts">
import LoginUtils from "@/utils/login/LoginUtils";
import Translator from "@/utils/common/Translator";
import ArrowIcon from "@/components/icons/navBarIcons/ArrowIcon.vue";
import {computed, onBeforeMount, ref} from "vue";
import {useDarkMode, useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {storeToRefs} from "pinia";
import {useRoute} from "vue-router";
import {Dialog, QExpansionItem} from "quasar";
import AboutNamaDialog from "@/components/dialogs/AboutNamaDialog.vue";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import QRInfoDialog from "@/components/dialogs/QRInfoDialog.vue";
import {useThemeStore} from "@/stores/ThemeDataStore.ts";
import router from "@/router";
import RouteNames from "@/router/RouteNames.ts";
import PlaceTokens from "@/utils/constants/PlaceTokens.ts";
import ViewMode from "@/utils/constants/ViewMode.ts";
import BSCEntities from "@/utils/constants/BSCEntities.ts";
import {UserMenuItem} from "@/utils/presenters/interfaces.ts";
import SettingsDialog from "@/components/dialogs/SettingsDialog.vue";
import {useLoginInfoStore} from "@/stores/loginInfo.ts";
import DTOVueGUITheme = namaTypes.DTOVueGUITheme;

const {darkMode} = useDarkMode();
const {isSideBarOpen} = storeToRefs(useNamaFlagsStore());
const route = useRoute();
const isPopupVisible = ref(false);
const themeList = ref<DTOVueGUITheme[]>([]);
const isExpanded = ref(false);
const currentTheme = ref(localStorage.getItem('theme-code'));

const themeMenu = computed<UserMenuItem[]>(() => {
  if (!themeList.value)
    return [];
  let menu: UserMenuItem[] = []
  for (let theme of themeList.value)
    menu.push({
      theme: theme,
      handler: () => applyTheme(theme.code)
    })
  return menu;
});

onBeforeMount(async () => {
  let list: DTOVueGUITheme[] | undefined = await useThemeStore().fetchListThemes();
  if (list)
    themeList.value = list;
})

async function redirectToSettings(e: PointerEvent | undefined) {
  if (e && e.ctrlKey) {
    window.open(router.resolve({name: RouteNames.SETTINGS_ROUTE}).href, "_blank");
  } else {
    Dialog.create({
      component: SettingsDialog,
    })
  }
  isPopupVisible.value = false;
}

async function showAboutDialog() {
  Dialog.create({
    component: AboutNamaDialog
  })
  isPopupVisible.value = false
}

async function applyTheme(code: string | undefined) {
  await useThemeStore().fetchAndApplyTheme(code);
  currentTheme.value = localStorage.getItem('theme-code');
}

function logout() {
  LoginUtils.logout();
}

function backToOldGui(ctrlKey?: boolean) {
  let fullPath = route.fullPath;
  fullPath = fullPath.substring(1, fullPath.indexOf("?"));
  let url = "erp" + (ctrlKey ? "5" : "1") + ".html?locale=" + (Translator.isArabic() ? "ar" : "en") + "#" + fullPath + ":";
  for (let queryKey in route.query) {
    if (route.query[queryKey] === null || route.query[queryKey] === "null" || route.query[queryKey] === undefined)
      continue;
    url += queryKey + "/" + route.query[queryKey] + "&";
  }
  window.open(url, "_blank");
}

const userMenu = ref<UserMenuItem[]>([
  {
    titleId: "settings",
    handler: (e) => redirectToSettings(e),
    defaultIcon: "nm-settings",
    hasDivider: true
  },
  {
    titleId: "darkMode",
    vModel: darkMode,
    defaultIcon: "nm-dark-mode",
    hasDivider: true
  },
  {
    titleId: "GUIThemesList",
    defaultIcon: "nm-theme-editor"
  },
  {
    titleId: "Qr Info",
    handler: (e) => {
      Dialog.create({
        component: QRInfoDialog
      })
    },
    defaultIcon: "qr_code"
  },
  {
    titleId: "backToOldGui",
    handler: (e) => backToOldGui(e?.ctrlKey),
    defaultIcon: "nm-old-pc"
  },
  {
    titleId: "about",
    handler: (e) => showAboutDialog(),
    defaultIcon: "info_outline"
  },
  {
    titleId: "logout",
    handler: (e) => logout(),
    defaultIcon: "nm-logout"
  }
]);

function MenuItemHandler(e: PointerEvent, item: UserMenuItem) {
  if (item.hasOwnProperty('vModel')) {
    item.vModel = !item.vModel
  } else if (item.handler) {
    item.handler(e);
      isPopupVisible.value = false;
  }
}

function themeHandler(item: UserMenuItem | undefined) {
  if (item && item.handler)
    item.handler()
}

async function goToGuiTheme() {
  isPopupVisible.value = false;
  await router.push({
    name: RouteNames.EDIT_VIEW_ROUTE,
    query: {
      [PlaceTokens.PRM_ENTITY_TYPE]: BSCEntities.GUITheme,
      [PlaceTokens.PRM_VIEW]: ViewMode.NEW,
    }
  });
}

function isGUIThemesList(titleId: string | undefined): boolean {
  return titleId === 'GUIThemesList'
}


</script>

<template>
  <div class="tw-flex tw-justify-between tw-items-center hover:tw-cursor-pointer">
    <div class="tw-pr-2 ultra:tw-pr-1">
      <q-avatar rounded :title="Translator.getName(LoginUtils.loginInfo?.userInfo?.name1 ?? '', LoginUtils.loginInfo?.userInfo?.name2 ?? '')"
                class="tw-size-8 compact:tw-size-[30px] ultra:tw-size-7 dark:tw-bg-gray-600 tw-bg-gray-200 tw-rounded-full">
        <img v-if="LoginUtils.userImageSrc.value" :src="LoginUtils.userImageSrc.value" alt="user"/>
        <NamaIcon v-else default-icon="nm-user"/>
      </q-avatar>
    </div>
    <span class="p-1 tw-text-[#000000] dark:tw-text-white ultra:tw-text-xs tablet:tw-hidden" :class="{'laptop:tw-hidden':isSideBarOpen}">{{
        Translator.getName(LoginUtils.loginInfo?.userInfo?.name1 ?? '', LoginUtils.loginInfo?.userInfo?.name2 ?? '')
      }}</span>
    <ArrowIcon :is-down="!isPopupVisible" class="compact:tw-size-[22px] ultra:tw-size-5"/>
    <q-menu v-if="useLoginInfoStore().isLoggedIn" v-model="isPopupVisible" :offset=[0,15]
            class="tw-min-w-[260px] tw-shadow-[0_7px_10px_0px_rgba(0,0,0,0.05)] dark:tw-shadow-[0_7px_10px_0px_rgba(255,255,255,0.05)] tw-ring-[1px] dark:tw-ring-white/10 tw-ring-black/10 tw-rounded-md mobile:tw-min-w-[220px] tw-w-[15%] tw-p-1"
            id="userSectionMenu">
      <template v-for="item in userMenu">
        <div class="tw-select-none flex justify-between tw-gap-1.5 tw-items-center !tw-py-1.5"
             :class="{'popoverItem !tw-px-1.5':!isGUIThemesList(item.titleId)}"
             @click.capture="(event) => MenuItemHandler(event, item)">
          <template v-if="isGUIThemesList(item.titleId)">
            <q-expansion-item
              dense
              v-model="isExpanded"
              class="tw-w-full tw-h-fit tw-border-[1px]"
              :class="[isExpanded? 'tw-border-primary-light dark:tw-border-primary-dark tw-rounded-md':'tw-border-transparent']"
              :header-class="[isExpanded ? 'nama-theme-list-expand tw-rounded-t-md tw-bg-namaBackground-bright-light dark:tw-bg-namaBackground-bright-dark' : 'nama-theme-list-expand tw-rounded-md']"
            >
              <template #header>
                <div class="tw-flex tw-items-center tw-gap-3 tw-w-full tw-pl-1.5">
                  <NamaIcon :default-icon="item.defaultIcon" class="tw-size-5"/>
                  <span v-text="Translator.translateId(item.titleId)"
                        class="tw-flex-grow "/>
                </div>
              </template>
              <q-space/>
              <q-card
                class="dark:tw-bg-namaBackground-deep-dark tw-bg-namaBackground-deep-light tw-text-[#000000] dark:tw-text-white hover:tw-text-[#000000]">
                <q-card-section class="tw-h-fit tw-max-h-32 tw-overflow-x-auto tw-p-2">
                  <template v-for="item in themeMenu">
                    <div class="popoverItem tw-select-none flex justify-between tw-gap-1.5 tw-items-center !tw-p-1.5"
                         :class="{'dark:tw-bg-namaBackground-dim-dark tw-bg-namaBackground-dim-light': item.theme?.code == currentTheme}"
                         @click.capture="themeHandler(item)">
                      <div class="tw-px-0">
                        <q-icon name="circle"
                                class="tw-text-[18px] tw-rounded-full tw-ring-1 tw-ring-[#000000] dark:tw-ring-white"
                                :style="{color: item.theme?.lightColorTheme?.primaryColor}"/>
                        <q-icon name="circle"
                                class="tw-text-[18px] tw-rounded-full tw-ring-1 tw-ring-[#000000] dark:tw-ring-white"
                                :style="{color: item.theme?.lightColorTheme?.primaryBackgroundColor}"/>
                        <q-icon name="circle"
                                class="tw-text-[18px] tw-rounded-full tw-ring-1 tw-ring-[#000000] dark:tw-ring-white"
                                :style="{color: item.theme?.darkColorTheme?.primaryColor}"/>
                        <q-icon name="circle"
                                class="tw-text-[18px] tw-rounded-full tw-ring-1 tw-ring-[#000000] dark:tw-ring-white"
                                :style="{color: item.theme?.darkColorTheme?.primaryBackgroundColor}"/>
                      </div>
                      <span v-text="Translator.getName(item.theme?.name1 ?? '', item.theme?.name2 ?? '')" class="tw-flex-grow"/>
                    </div>
                  </template>
                  <div class="popoverItem tw-select-none flex justify-between tw-gap-1.5 tw-items-center !tw-p-1.5"
                       @click.capture="goToGuiTheme()">
                    <NamaIcon default-icon="add" class="tw-size-5"/>
                    <span v-text="Translator.translateId('addNew')" class="tw-flex-grow"/>
                  </div>
                </q-card-section>
              </q-card>
            </q-expansion-item>
          </template>
          <template v-else>
            <NamaIcon :default-icon="item.defaultIcon" class="tw-size-5"/>
            <span v-text="Translator.translateId(item.titleId)" class="tw-flex-grow"/>
            <q-toggle :id="item.titleId" v-if="item.hasOwnProperty('vModel')" color="primary" :model-value="item.vModel" dense/>
          </template>
        </div>
        <q-separator v-if="item.hasDivider" class=" tw-my-1 tw-bg-black/15 dark:tw-bg-namaBackground-deep-light/35"/>
      </template>
    </q-menu>
  </div>
</template>
